import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangeFilter.css'; // Reusing the global datepicker styles
import aviso, { textoDoErro } from '../lib/aviso';
import Select from './Select';
import { temFuncionalidade } from '../config/permissions';
import { TipoContatoService, type TipoContato } from '../services/TipoContatoService';
import { etapaDeEntrada, etapasDoMiolo, nomeEtapa } from '../data/etapas';
import './DetailsPanel.css';
import {
  LuX,
  LuPhone,
  LuTag,
  LuStickyNote,
  LuFileText,
  LuCalendarPlus,
  LuPaperclip,
  LuUpload,
  LuImage,
  LuMusic,
  LuVideo,
  LuFile,
  LuEye,
  LuDownload,
  LuTrash2,
  LuPlus,
} from 'react-icons/lu';
import type { Contact } from '../data/mockData';
import type { Detail } from '../types/Details';
import { DetailsService } from '../services/DetailsService';
import { AttachmentService } from '../services/AttachmentService';
import { AgendaService } from '../services/AgendaService';
import type { ContatoAnexoResponseDTO, TipoAnexo } from '../types/Attachment';
import SummaryModal from './SummaryModal';

export type SecaoContato = 'negociacao' | 'anotacoes' | 'agenda' | 'anexos';

interface DetailsPanelProps {
  contact: Contact;
  onClose: () => void;
  onUpdate?: () => void;
  /** Seção que o trilho pediu para abrir; a coluna rola até ela. */
  secaoFoco?: SecaoContato;
  /** Avisa que já rolou, para o pedido não ficar preso no mesmo valor. */
  onFocoAtendido?: () => void;
  /** Tira/leva o contato do funil; usado pelo botão "Levar para o funil" quando o contato está fora. */
  onFunilChange?: (contactId: number, foraDoFunil: boolean) => Promise<void>;
}

const DetailsPanel: React.FC<DetailsPanelProps> = ({ contact, onClose, onUpdate, secaoFoco, onFocoAtendido, onFunilChange }) => {
  const foraDoFunil = contact.foraDoFunil === true;

  /* Tipo do contato: a lista é da empresa e vem uma vez; o painel mostra o nome pelo id que a
     listagem já trouxe, sem pedir nada a mais por conversa. */
  const usaTipoContato = temFuncionalidade('tipo_contato');
  const [tiposContato, setTiposContato] = useState<TipoContato[]>([]);
  const [salvandoTipo, setSalvandoTipo] = useState(false);

  useEffect(() => {
    if (!usaTipoContato) return;
    let vivo = true;
    TipoContatoService.getAll()
      .then(r => { if (vivo && r.sucesso) setTiposContato(r.dados || []); })
      .catch(() => { /* o painel abre do mesmo jeito */ });
    return () => { vivo = false; };
  }, [usaTipoContato]);

  const definirTipoContato = async (valor: string) => {
    if (!contact.contactId) return;
    setSalvandoTipo(true);
    try {
      const r = await TipoContatoService.definirNoContato(contact.contactId, valor === '0' ? null : parseInt(valor));
      aviso.ok(r.mensagem || 'Tipo do contato atualizado.');
      onUpdate?.();
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível atualizar o tipo do contato.'));
    } finally {
      setSalvandoTipo(false);
    }
  };
  const formatCurrency = (value: string | number) => {
    if (value === '' || value === undefined || value === null) return '';

    if (typeof value === 'number') {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    }

    const onlyDigits = String(value).replace(/\D/g, '');
    if (onlyDigits === '') return '';

    const numberValue = Number(onlyDigits) / 100;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numberValue);
  };

  const [status, setStatus] = useState(contact.statusId ? String(contact.statusId) : String(etapaDeEntrada()));
  const [dealValue, setDealValue] = useState(contact.detailsValue ? formatCurrency(contact.detailsValue) : '');
  const [newNote, setNewNote] = useState('');

  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [observations, setObservations] = useState<Detail[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState('');

  const [appointments, setAppointments] = useState<any[]>([]);
  const [isLoadingAgenda, setIsLoadingAgenda] = useState(false);
  const [novoAgendamento, setNovoAgendamento] = useState(false);
  const [newAppTitle, setNewAppTitle] = useState('');
  const [newAppDate, setNewAppDate] = useState<Date | null>(new Date());
  const [newAppTime, setNewAppTime] = useState('09:00');
  const [isSavingApp, setIsSavingApp] = useState(false);

  const [attachments, setAttachments] = useState<ContatoAnexoResponseDTO[]>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const corpoRef = useRef<HTMLDivElement>(null);

  /* Agrupado desde a padronização de selects (2026-09-12): sete status numa
     lista corrida não deixam ver que "Não iniciado" é atendimento e "Ganho" é
     desfecho. O ponto de cor que o desenho previa ficou de fora — o produto
     não tem paleta de status, e inventar sete cores aqui seria decidir isso
     de lado. */
  /* O funil da empresa: a porta (robô e entrada do lead) em Atendimento, o resto do miolo e os
     desfechos em Negociação. Etapa criada ou renomeada pela empresa aparece com o nome dela. */
  const entrada = etapaDeEntrada();
  const statusOptions = [
    { value: String(entrada), label: nomeEtapa(entrada), group: 'Atendimento' },
    { value: '1', label: nomeEtapa(1), group: 'Atendimento' },
    { value: '8', label: nomeEtapa(8), group: 'Atendimento' },
    ...etapasDoMiolo().filter(id => id !== entrada)
      .map(id => ({ value: String(id), label: nomeEtapa(id), group: 'Negociação' })),
    { value: '3', label: nomeEtapa(3), group: 'Negociação' },
    { value: '6', label: nomeEtapa(6), group: 'Negociação' },
  ];
  // Conversa parada numa etapa que saiu do funil (oculta ou apagada) ainda mostra onde está.
  if (!statusOptions.some(o => o.value === status)) {
    statusOptions.push({ value: status, label: nomeEtapa(Number(status)), group: 'Negociação' });
  }

  useEffect(() => {
    if (contact) {
      setStatus(contact.statusId ? String(contact.statusId) : String(etapaDeEntrada()));
      setDealValue(contact.detailsValue ? formatCurrency(contact.detailsValue) : '');
      setIsDirty(false);
    }
  }, [contact]);

  const fetchObservations = useCallback(async () => {
    if (!contact) return;
    setIsLoadingHistory(true);
    setHistoryError(null);
    try {
      const data = await DetailsService.getDetailsByConversationId(contact.id);
      setObservations(data);
    } catch (err: any) {
      setHistoryError(err.message);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [contact]);

  const fetchAttachments = useCallback(async () => {
    if (!contact) return;
    setIsLoadingAttachments(true);
    setAttachmentError(null);
    try {
      const data = await AttachmentService.getAttachmentsByConversation(contact.id);
      setAttachments(data);
    } catch (err: any) {
      setAttachmentError(err.message);
    } finally {
      setIsLoadingAttachments(false);
    }
  }, [contact]);

  const fetchAppointments = useCallback(async () => {
    if (!contact) return;
    setIsLoadingAgenda(true);
    try {
      const data = await AgendaService.getEventsByConversation(contact.id);
      setAppointments(data);
    } catch (err) {
      console.error('Error fetching conversation events:', err);
    } finally {
      setIsLoadingAgenda(false);
    }
  }, [contact]);

  /* As seções são empilhadas, não abas: o painel carrega as três de uma vez
     quando abre. São as mesmas chamadas que as abas faziam, só que sem
     depender de o vendedor descobrir a aba. */
  useEffect(() => {
    fetchObservations();
    fetchAttachments();
    fetchAppointments();
  }, [fetchObservations, fetchAttachments, fetchAppointments]);

  /* Rolar até a seção que o trilho pediu.
     Duas armadilhas que fizeram isto não funcionar na primeira versão:
     1. no primeiro clique o painel MONTA junto, e as três listas ainda estão
        carregando — rolar antes disso mira uma altura que vai mudar;
     2. `scrollIntoView` mexe em todos os ancestrais roláveis. Aqui o alvo é só
        a coluna do painel, então o deslocamento é calculado na mão. */
  useEffect(() => {
    const corpo = corpoRef.current;
    if (!secaoFoco || !corpo) return;
    if (isLoadingHistory || isLoadingAgenda || isLoadingAttachments) return;

    const alvo = corpo.querySelector(`#ch-sec-${secaoFoco}`);
    if (!alvo) return;

    const rolar = (suave: boolean) => {
      const delta = alvo.getBoundingClientRect().top - corpo.getBoundingClientRect().top;
      if (Math.abs(delta) < 2) return;
      corpo.scrollTo({ top: corpo.scrollTop + delta, behavior: suave ? 'smooth' : 'auto' });
    };

    const quadro = requestAnimationFrame(() => rolar(true));
    /* Segunda passada: medido numa janela de 620px, a primeira rolagem parou em
       215px de 288 possíveis, porque as seções ainda ganhavam altura depois do
       cálculo. A correção é no-op quando a primeira já acertou. */
    const correcao = window.setTimeout(() => {
      rolar(false);
      // Libera o pedido: sem isto, clicar no MESMO ícone de novo não rolaria,
      // porque `secaoFoco` não mudaria de valor.
      onFocoAtendido?.();
    }, 400);

    return () => {
      cancelAnimationFrame(quadro);
      clearTimeout(correcao);
    };
  }, [secaoFoco, isLoadingHistory, isLoadingAgenda, isLoadingAttachments, onFocoAtendido]);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDealValue(formatCurrency(e.target.value));
    setIsDirty(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let valor = 0;
      if (typeof dealValue === 'string') {
        const onlyDigits = dealValue.replace(/\D/g, '');
        valor = onlyDigits ? Number(onlyDigits) / 100 : 0;
      } else {
        valor = Number(dealValue) || 0;
      }

      const previousValue = contact.detailsValue || 0;
      let descriptionToSend = newNote;

      // Fora do funil não há valor nem etapa a mudar: só a anotação vai (o servidor também trava).
      if (!foraDoFunil && valor !== previousValue) {
        const formattedPrevious = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(previousValue);
        const formattedNew = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
        const autoNote = `Alteração de valor: De ${formattedPrevious} para ${formattedNew}`;
        descriptionToSend = descriptionToSend.trim() ? `${descriptionToSend}\n\n${autoNote}` : autoNote;
      }

      await DetailsService.addDetail({
        idConversa: contact.id,
        descricao: descriptionToSend,
        statusNegociacaoId: foraDoFunil ? (contact.statusId || etapaDeEntrada()) : parseInt(status),
        valor: foraDoFunil ? (contact.detailsValue || 0) : valor,
      });

      setNewNote('');
      fetchObservations();
      onUpdate?.();

      aviso.ok('Alterações salvas.');
      setIsDirty(false);
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível salvar a observação.'));
    } finally {
      setIsSaving(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const handleOpenSummary = (content: string) => {
    setSelectedSummary(content);
    setIsSummaryModalOpen(true);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !contact) return;

    setIsUploading(true);
    try {
      let tipo: TipoAnexo = 'outros';
      if (file.type.startsWith('image/')) tipo = 'image';
      else if (file.type.startsWith('audio/')) tipo = 'audio';
      else if (file.type.startsWith('video/')) tipo = 'video';
      else if (file.type === 'application/pdf' || file.type.includes('msword') || file.type.includes('officedocument'))
        tipo = 'documento';

      await AttachmentService.addAttachmentByConversation(contact.id, file, tipo);
      aviso.ok('Arquivo enviado.');
      fetchAttachments();
    } catch (error: any) {
      aviso.erro(textoDoErro(error, 'Não foi possível enviar o arquivo.'));
    } finally {
      setIsUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleCreateAppointment = async () => {
    if (!newAppTitle || !newAppDate || !contact) return;

    setIsSavingApp(true);
    try {
      const [year, month, day] = format(newAppDate, 'yyyy-MM-dd').split('-').map(Number);
      const [hours, minutes] = newAppTime.split(':').map(Number);
      const startDate = new Date(year, month - 1, day, hours, minutes);

      const startDateTime = startDate.toISOString();
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
      const endDateTime = endDate.toISOString();

      const result = await AgendaService.createEventByConversation(contact.id, {
        descricao: newAppTitle,
        dataInicio: startDateTime,
        dataFim: endDateTime,
        detalhes: 'Agendado via chat',
      });

      if (result.sucesso) {
        aviso.ok('Agendamento criado.');
        setNewAppTitle('');
        setNewAppDate(new Date());
        setNewAppTime('09:00');
        setNovoAgendamento(false);
        fetchAppointments();
      } else {
        aviso.erro(result.mensagem || 'Não foi possível criar o agendamento.');
      }
    } catch (error) {
      aviso.erro('Não foi possível salvar o agendamento.');
    } finally {
      setIsSavingApp(false);
    }
  };

  const handleRemoveAppointment = async (id: number) => {
    if (!window.confirm('Remover este agendamento?')) return;
    try {
      await AgendaService.remove(id);
      aviso.ok('Agendamento removido.');
      fetchAppointments();
    } catch (error) {
      aviso.erro('Não foi possível remover o agendamento.');
    }
  };

  const handleDownloadAttachment = async (id: number, filename: string) => {
    try {
      const url = await AttachmentService.getAttachmentFileUrl(id);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.split('/').pop() || 'anexo';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      aviso.erro('Não foi possível baixar o arquivo.');
    }
  };

  const handleViewAttachment = async (id: number) => {
    try {
      const url = await AttachmentService.getAttachmentFileUrl(id);
      window.open(url, '_blank');
    } catch (error: any) {
      aviso.erro('Não foi possível abrir o arquivo.');
    }
  };

  const handleRemoveAttachment = async (id: number) => {
    if (!window.confirm('Deseja realmente remover este anexo?')) return;
    try {
      await AttachmentService.removeAttachment(id);
      aviso.ok('Anexo removido.');
      fetchAttachments();
    } catch (error: any) {
      aviso.erro('Não foi possível remover o anexo.');
    }
  };

  const iconeAnexo = (tipo: string) => {
    switch (tipo) {
      case 'image':
        return <LuImage size={14} />;
      case 'audio':
        return <LuMusic size={14} />;
      case 'video':
        return <LuVideo size={14} />;
      case 'documento':
        return <LuFileText size={14} />;
      default:
        return <LuFile size={14} />;
    }
  };

  return (
    <aside className="ch-painel">
      <div className="ch-painel-h">
        <div style={{ minWidth: 0 }}>
          <div className="ch-painel-t">Contato</div>
          <div className="ch-painel-s">{contact.name}</div>
        </div>
        <button type="button" className="ch-bt ico" onClick={onClose} title="Fechar painel" style={{ marginLeft: 'auto' }}>
          <LuX size={15} />
        </button>
      </div>

      <div className="ch-painel-corpo" ref={corpoRef}>
        <section className="ch-sec" id="ch-sec-negociacao">
          <div className="ch-sec-t">
            <LuPhone size={12} />
            Identificação
          </div>
          <div className="ch-kv">
            <span className="k">Telefone</span>
            <span className="v">{contact.phone || '—'}</span>
            <span className="k">Responsável</span>
            <span className="v">{contact.responsibleName || '—'}</span>
            {usaTipoContato && tiposContato.length > 0 && contact.contactId && (
              <>
                <span className="k">Tipo</span>
                <span className="v">
                  <Select
                    size="control" block={false}
                    value={String(contact.tipoContatoId ?? 0)}
                    options={[
                      { value: '0', label: 'Sem tipo' },
                      ...tiposContato.map(t => ({ value: String(t.tipoContatoId), label: t.nome })),
                    ]}
                    onChange={definirTipoContato}
                    disabled={salvandoTipo}
                    ariaLabel="Tipo do contato" title="O que este contato é para a empresa" />
                </span>
              </>
            )}
          </div>
        </section>

        <section className="ch-sec">
          <div className="ch-sec-t">
            <LuTag size={12} />
            Negociação
          </div>
          {foraDoFunil ? (
            <>
              <p className="ch-vazio">Fora do funil — não conta na Pipeline nem nas métricas.</p>
              {contact.contactId && onFunilChange && (
                <button
                  type="button"
                  className="ch-bt"
                  onClick={async () => {
                    try {
                      await onFunilChange(contact.contactId!, false);
                      aviso.ok('Contato de volta ao funil.');
                    } catch (error: any) {
                      aviso.erro(textoDoErro(error, 'Não foi possível levar o contato para o funil.'));
                    }
                  }}
                >
                  Levar para o funil
                </button>
              )}
            </>
          ) : (
            <>
              <div className="ch-campo">
                <label htmlFor="ch-status">Status</label>
                <Select
                  id="ch-status"
                  size="sm"
                  value={status}
                  options={statusOptions}
                  onChange={v => {
                    setStatus(v);
                    setIsDirty(true);
                  }}
                />
              </div>
              <div className="ch-campo">
                <label htmlFor="ch-valor">Valor</label>
                <input id="ch-valor" type="text" value={dealValue} onChange={handleValueChange} placeholder="R$ 0,00" />
              </div>
            </>
          )}
        </section>

        <section className="ch-sec" id="ch-sec-anotacoes">
          <div className="ch-sec-t">
            <LuStickyNote size={12} />
            Anotações
            <span className="conta">{observations.length}</span>
          </div>

          <div className="ch-campo">
            <textarea
              id="ch-anotacao"
              rows={3}
              placeholder="Escreva uma anotação…"
              value={newNote}
              onChange={e => {
                setNewNote(e.target.value);
                setIsDirty(true);
              }}
              disabled={isSaving}
            />
          </div>

          {isLoadingHistory && <p className="ch-vazio">Carregando anotações…</p>}
          {historyError && <p className="ch-vazio">Erro: {historyError}</p>}
          {!isLoadingHistory && !historyError && observations.length === 0 && (
            <p className="ch-vazio">Nenhuma anotação nesta conversa.</p>
          )}
          {[...observations]
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .map(obs => {
              const isSummary = obs.content.startsWith('Resumo gerado pelo sistema');
              return (
                <div key={obs.id} className="ch-item" style={{ alignItems: 'flex-start' }}>
                  <span style={{ marginTop: 2, flex: 'none', color: 'var(--ch-faint)' }}>
                    {isSummary ? <LuFileText size={14} /> : <LuStickyNote size={14} />}
                  </span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    {isSummary ? (
                      <button type="button" className="ch-link" onClick={() => handleOpenSummary(obs.content)}>
                        Ver resumo da conversa
                      </button>
                    ) : (
                      <span style={{ display: 'block', whiteSpace: 'pre-wrap' }}>{obs.content}</span>
                    )}
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ch-muted)', marginTop: 2 }}>
                      {obs.usuario?.name || obs.usuario?.nome || `Usuário ${obs.userId}`} · {formatDateTime(obs.createdAt)}
                    </span>
                  </span>
                </div>
              );
            })}
        </section>

        <section className="ch-sec" id="ch-sec-agenda">
          <div className="ch-sec-t">
            <LuCalendarPlus size={12} />
            Agenda
            <span className="conta">{appointments.length}</span>
          </div>

          {isLoadingAgenda && <p className="ch-vazio">Buscando agenda…</p>}
          {!isLoadingAgenda && appointments.length === 0 && <p className="ch-vazio">Nenhum agendamento para este contato.</p>}
          {appointments.map(app => (
            <div key={app.agendaId} className="ch-item">
              <span className="ch-item-txt">{app.descricao}</span>
              <span className="m">{formatDateTime(app.dataInicio)}</span>
              <button
                type="button"
                className="ch-cmp-ico"
                onClick={() => handleRemoveAppointment(app.agendaId)}
                title="Remover agendamento"
              >
                <LuTrash2 size={14} />
              </button>
            </div>
          ))}

          {novoAgendamento ? (
            <div style={{ marginTop: 10 }}>
              <div className="ch-campo">
                <label htmlFor="ch-app-titulo">Descrição</label>
                <input
                  id="ch-app-titulo"
                  type="text"
                  placeholder="Ex.: reunião de fechamento"
                  value={newAppTitle}
                  onChange={e => setNewAppTitle(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div className="ch-campo" style={{ flex: 2 }}>
                  <label htmlFor="ch-app-data">Data</label>
                  <DatePicker
                    id="ch-app-data"
                    selected={newAppDate}
                    onChange={(date: Date | null) => setNewAppDate(date)}
                    dateFormat="dd/MM/yyyy"
                    locale={ptBR}
                    className="ch-datepicker"
                  />
                </div>
                <div className="ch-campo" style={{ flex: 1 }}>
                  <label htmlFor="ch-app-hora">Hora</label>
                  <input id="ch-app-hora" type="time" value={newAppTime} onChange={e => setNewAppTime(e.target.value)} />
                </div>
              </div>
              {/* O formulário grava dataFim = início + 1h fixo — mesma regra da Agenda. */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="ch-acao" onClick={() => setNovoAgendamento(false)}>
                  Cancelar
                </button>
                <button type="button" className="ch-salvar" onClick={handleCreateAppointment} disabled={isSavingApp || !newAppTitle}>
                  {isSavingApp ? 'Salvando…' : 'Agendar'}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="ch-link" onClick={() => setNovoAgendamento(true)}>
              <LuPlus size={13} />
              Agendar
            </button>
          )}
        </section>

        <section className="ch-sec" id="ch-sec-anexos">
          <div className="ch-sec-t">
            <LuPaperclip size={12} />
            Anexos
            <span className="conta">{attachments.length}</span>
          </div>

          {isLoadingAttachments && <p className="ch-vazio">Carregando anexos…</p>}
          {attachmentError && <p className="ch-vazio">Erro: {attachmentError}</p>}
          {!isLoadingAttachments && !attachmentError && attachments.length === 0 && (
            <p className="ch-vazio">Nenhum arquivo anexado.</p>
          )}
          {attachments.map(att => (
            <div key={att.id} className="ch-item">
              <span style={{ flex: 'none', color: 'var(--ch-faint)' }}>{iconeAnexo(att.tipoAnexo)}</span>
              <span className="ch-item-txt" title={att.caminhoAnexo}>
                {att.caminhoAnexo.split('/').pop()}
              </span>
              <span className="m" style={{ display: 'flex', gap: 4 }}>
                <button type="button" className="ch-cmp-ico" onClick={() => handleViewAttachment(att.id)} title="Visualizar">
                  <LuEye size={14} />
                </button>
                <button
                  type="button"
                  className="ch-cmp-ico"
                  onClick={() => handleDownloadAttachment(att.id, att.caminhoAnexo)}
                  title="Baixar"
                >
                  <LuDownload size={14} />
                </button>
                <button type="button" className="ch-cmp-ico" onClick={() => handleRemoveAttachment(att.id)} title="Remover">
                  <LuTrash2 size={14} />
                </button>
              </span>
            </div>
          ))}

          {/* O botão de anexar aparece mesmo sem nenhum arquivo — sem ele não
              há como começar. */}
          <label className="ch-link" style={{ marginTop: 8, cursor: isUploading ? 'default' : 'pointer' }}>
            <LuUpload size={13} />
            {isUploading ? 'Enviando…' : 'Anexar arquivo'}
            <input type="file" onChange={handleFileUpload} disabled={isUploading} style={{ display: 'none' }} />
          </label>
        </section>
      </div>

      <div className="ch-painel-f">
        <button type="button" className="ch-salvar" onClick={handleSave} disabled={!isDirty || isSaving}>
          {isSaving ? 'Salvando…' : isDirty ? 'Salvar' : 'Salvo'}
        </button>
      </div>

      <SummaryModal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        summary={selectedSummary}
      />
    </aside>
  );
};

export default DetailsPanel;
